import * as React from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { MeatballMenu } from 'ui';
import { createFilterMenuOptions } from 'app/components/filters/filters-panel/filter-menu-options';
import { FilterOption, FilterMenuOptions, FilterMenuOptionsTypes, Filter } from 'app/components/filters/types';
import styles from './filter-panel.scss';

export interface FilterPanelProps {
  id: string;
  filter: Filter;
  count: number;
  isOpen: boolean;
  translate: any;
  added?:boolean;
  onFilterMenuOption?: (id: string, option: FilterMenuOptions, type: FilterMenuOptionsTypes) => void;
  onChange: (id: string, value: boolean) => void;
  onRemove: (id: string) => void;
  children?: React.ReactNode;
}

export interface FilterState {
  isClosing: boolean;
  animationPlayed: boolean;
}

export class FilterPanel extends React.Component<FilterPanelProps, FilterState> {
  public state: Readonly<FilterState> = {
    isClosing: false,
    animationPlayed: false
  };

  private onClick = (e: React.SyntheticEvent<any>) => {
    if (!this.props.onChange) {
      return;
    }
    e.preventDefault();
    if (this.props.isOpen) {
      this.setState({ isClosing: true });

      setTimeout(() => {
        this.setState({
          isClosing: false
        });
        return this.props.onChange(this.props.id, false);
      }, 300);
    } else {
      return this.props.onChange(this.props.id, true);
    }
  };

  private onAnimationEnd = () => {
    this.setState({animationPlayed: true});
  };

  private handleRemove = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    this.props.onRemove(this.props.id);
  };

  private handleInclude = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    this.props.onFilterMenuOption(this.props.id, FilterMenuOptions.Include, FilterMenuOptionsTypes.MainLevel);
  };

  private handleExclude = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    this.props.onFilterMenuOption(this.props.id, FilterMenuOptions.Exclude, FilterMenuOptionsTypes.MainLevel);
  };

  private handleIncludeSublevels = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    this.props.onFilterMenuOption(this.props.id, FilterMenuOptions.IncludeSubLevels, FilterMenuOptionsTypes.SubLevel);
  };

  private handleExcludeSublevels = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    this.props.onFilterMenuOption(this.props.id, FilterMenuOptions.ExcludeSubLevels, FilterMenuOptionsTypes.SubLevel);
  };

  private hasPermission = (option: FilterMenuOptions, options: FilterMenuOptions[]) => {
    return options && options.length > 0 && options.indexOf(option) > -1;
  };

  private hasSelectedOptions = (option: FilterMenuOptions, options: FilterOption[]) => {
    return options.findIndex(item => item.option === option) > -1;
  };

  private hasEmptyMenuOptionsType = (type: FilterMenuOptionsTypes, options: FilterOption[]) => {
    return options.findIndex(item => item.type === type) === -1;
  };

  // eslint:disable jsx-no-multiline-js max-line-length
  private renderMenuOptions = (options: FilterOption[]) => {
    const descriptions = {
      [FilterMenuOptions.Exclude]: this.props.translate.filter_menu_options_desc_exclude,
      [FilterMenuOptions.ExcludeSubLevels]: this.props.translate.filter_menu_options_desc_exclude_sub
    };

    return <ul>
      {options.map((item, index) => <li key={index}>
        <span>{descriptions[item.option]}</span>
      </li>)}</ul>;
  };

  private renderMenu = (options: FilterMenuOptions[], selectedOptions: FilterOption[]) => (
    <div className={styles.filterMeatballMenu}>
      <MeatballMenu closeAfterClick>
        {this.hasPermission(FilterMenuOptions.Clear, options) &&
          <li><button type="button" className="dropdown-item" onClick={this.handleRemove}>
            {this.props.translate.remove_filter}
          </button></li>}
        {this.hasPermission(FilterMenuOptions.Include, options) &&
          this.hasSelectedOptions(FilterMenuOptions.Exclude, selectedOptions) &&
          <li><button type="button" className="dropdown-item" onClick={this.handleInclude}>
            {this.props.translate.filter_menu_option_include}
          </button></li>}
        {this.hasPermission(FilterMenuOptions.Exclude, options) &&
          (this.hasSelectedOptions(FilterMenuOptions.Include, selectedOptions)
            || this.hasEmptyMenuOptionsType(FilterMenuOptionsTypes.MainLevel, selectedOptions)) &&
            <li><button type="button" className="dropdown-item" onClick={this.handleExclude}>
            {this.props.translate.filter_menu_option_exclude}
          </button></li>}
        {this.hasPermission(FilterMenuOptions.IncludeSubLevels, options) &&
          this.hasSelectedOptions(FilterMenuOptions.ExcludeSubLevels, selectedOptions) &&
          <li><button type="button" className="dropdown-item" onClick={this.handleIncludeSublevels}>
            {this.props.translate.filter_menu_option_include_sub}
          </button></li>}
        {this.hasPermission(FilterMenuOptions.ExcludeSubLevels, options) &&
          (this.hasSelectedOptions(FilterMenuOptions.IncludeSubLevels, selectedOptions)
            || this.hasEmptyMenuOptionsType(FilterMenuOptionsTypes.SubLevel, selectedOptions)) &&
            <li><button type="button" className="dropdown-item" onClick={this.handleExcludeSublevels}>
            {this.props.translate.filter_menu_option_exclude_sub}
          </button></li>}
      </MeatballMenu>
    </div>);

  public render() {
    const { filterPanel, titleStyle } = styles;
    const { isOpen, count, children, onChange, filter } = this.props;
    const { label, advancedSearchType, menuOptions } = this.props.filter;

    const animateOutStyles = this.state.isClosing ? styles.animateOut : '';
    const isOpenStyles = isOpen ? styles.isOpen : styles.isClosed;

    const newlyAdded = this.props.added && !this.state.animationPlayed;

    return <div className={`${filterPanel} filter-panel`}>
      <button type="button" 
        className={`${titleStyle} ${newlyAdded ? styles.added : ''}`}
        onClick={this.onClick} onAnimationEnd={this.onAnimationEnd}
      >
        {filter?.required && <span className="asterisk-required">* </span>}
          {label}
          {count > 0 && <span> ({count})</span>
        }
        {onChange && <FiChevronDown className={isOpen && !this.state.isClosing ? 'expand-icon-down' : 'expand-icon-up'}/>}
      </button>

      <div className={`${animateOutStyles} ${isOpenStyles} ${styles.contentWrapper}`}>
        <div className={styles.content}>
            <div className="clearfix">
              <div className="float-left">
                {menuOptions && this.renderMenuOptions(menuOptions)}
              </div>
              <div className="float-right">
                {menuOptions && this.renderMenu(createFilterMenuOptions(advancedSearchType), menuOptions)}
              </div>
            </div>
            {children}
        </div>
        </div>
    </div>;
  }
}
